import { useState, useEffect } from "react";
import { doc, updateDoc, increment, collection, query, where, getDocs, limit } from "firebase/firestore";
import { auth, db } from "../firebase";
import { calcXp, BADGE_RULES } from "../lib/xp.js";

export default function GamePage({ unitId, onDone }) {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [quizComplete, setQuizComplete] = useState(false);

  // Load questions for this unit
  useEffect(() => {
    async function loadQuestions() {
      try {
        setLoading(true);
        
        // Try to load questions from Firestore
        const questionsRef = collection(db, "questions");
        const questionsQuery = query(
          questionsRef,
          where("unitId", "==", unitId),
          limit(5)
        );
        
        const questionsSnap = await getDocs(questionsQuery);
        
        if (!questionsSnap.empty) {
          const loadedQuestions = [];
          questionsSnap.forEach(doc => {
            loadedQuestions.push({
              id: doc.id,
              ...doc.data()
            });
          });
          setQuestions(loadedQuestions);
        } else {
          // If no questions found, create placeholder questions
          const placeholderQuestions = [];
          for (let i = 1; i <= 5; i++) {
            placeholderQuestions.push({
              id: `placeholder-${i}`,
              question: getPlaceholderQuestion(unitId, i),
              choices: ["3", "4", "5", "6"],
              answer: "5",
            });
          }
          setQuestions(placeholderQuestions);
        }
      } catch (error) {
        console.error("Error loading questions:", error);
      } finally {
        setLoading(false);
      }
    }
    
    loadQuestions();
  }, [unitId]);
  
  // Generate a placeholder question based on unit type
  function getPlaceholderQuestion(unitId, num) {
    if (unitId.includes("add_sub")) {
      return `Question ${num}: What is ${num + 1} + ${num} ?`;
    } else if (unitId.includes("shapes")) {
      return `Question ${num}: How many sides does a triangle have?`;
    } else if (unitId.includes("measure")) {
      return `Question ${num}: Which is longer, a pencil or a bus?`;
    } else {
      return `Question ${num}: What is 2 + 3?`;
    }
  }

  // Handle answer selection
  async function handleAnswer(choice) {
    const currentQuestion = questions[currentIndex];
    const isCorrect = choice === currentQuestion.answer;
    
    // Show feedback
    setFeedback({
      correct: isCorrect,
      message: isCorrect ? "Correct!" : `Incorrect. The answer is ${currentQuestion.answer}`
    });
    
    // Update score
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    
    // Move to next question after a delay
    setTimeout(() => {
      setFeedback(null);
      
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        // Quiz complete
        setQuizComplete(true);
      }
    }, 1500);
  }

  // Award XP and complete the unit
  async function finishQuiz() {
    try {
      // Calculate XP earned
      const xpEarned = calcXp(score, questions.length);
      
      // Update user's XP in Firestore
      const userRef = doc(db, "users", auth.currentUser.uid);
      await updateDoc(userRef, { 
        xp: increment(xpEarned),
        lastUnitCompleted: unitId
      });
      
      // Check for new badges
      // (In a real app, you would check current XP and award badges)
      
      // Return to dashboard with score
      onDone(score);
    } catch (error) {
      console.error("Error awarding XP:", error);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading questions...</div>
      </div>
    );
  }

  if (quizComplete) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg text-center">
          <h1 className="text-2xl font-bold mb-4">Quiz Complete!</h1>
          <p className="text-lg mb-4">You got {score} out of {questions.length} correct.</p>
          <p className="text-green-600 font-bold mb-6">
            XP Earned: +{calcXp(score, questions.length)}
          </p>
          
          <button
            onClick={finishQuiz}
            className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-6">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
        <div className="flex justify-between mb-4">
          <span className="font-bold">Question {currentIndex + 1}/{questions.length}</span>
          <span className="font-bold">Score: {score}</span>
        </div>
        
        <h3 className="text-xl font-bold mb-4">{currentQuestion.question}</h3>
        
        <div className="space-y-2 mb-4">
          {currentQuestion.choices.map((choice) => (
            <button
              key={choice}
              onClick={() => !feedback && handleAnswer(choice)}
              disabled={!!feedback}
              className={`w-full p-3 rounded text-left ${
                feedback && choice === currentQuestion.answer
                  ? "bg-green-100 border border-green-500"
                  : feedback && choice !== currentQuestion.answer
                  ? "bg-gray-100"
                  : "bg-blue-50 hover:bg-blue-100"
              }`}
            >
              {choice}
            </button>
          ))}
        </div>
        
        {feedback && (
          <div className={`p-3 rounded ${
            feedback.correct ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}>
            {feedback.message}
          </div>
        )}
      </div>
    </div>
  );
}